import React, { useState, createContext, useContext } from 'react';
import { CartItemType } from '../types/types';
interface CartContextType {
  items: CartItemType[];
  isCartOpen: boolean;
  addItem: (item: CartItemType) => void;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, delta: number) => void;
  clearCart: () => void;
  toggleCart: () => void;
  cartTotal: number;
  cartItemCount: number;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [items, setItems] = useState<CartItemType[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const addItem = (item: CartItemType) => {
    setItems(prev => {
      // Check if identical item exists (same id or same configuration)
      // For simplicity, we'll just add as new unless we implement deep equality check
      // But we can check if it's an exact edit replacement
      const existingIndex = prev.findIndex(i => i.id === item.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = item;
        return newItems;
      }
      return [...prev, item];
    });
    // Optional: show toast here
  };
  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };
  const updateQuantity = (id: string, delta: number) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return {
          ...item,
          quantity: newQuantity
        };
      }
      return item;
    }));
  };
  const clearCart = () => {
    setItems([]);
  };
  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };
  const cartTotal = items.reduce((sum, item) => sum + item.subtotal * item.quantity, 0);
  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
  return <CartContext.Provider value={{
    items,
    isCartOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    toggleCart,
    cartTotal,
    cartItemCount
  }} data-id="element-450">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}